import { useState, useEffect } from "react";
import { useMyLoads } from "@/hooks/useMyLoads";
import { MetricCard } from "@/components/dashboard/MetricCardV2";
import { HeatMapModule } from "@/components/dashboard/HeatMapModule";
import { LaneSummaryCard } from "@/components/dashboard/LaneSummaryCard";
import { LoadsTableV2 } from "@/components/dashboard/LoadsTableV2";
import { AIQuickActionsPanel } from "@/components/dashboard/AIQuickActionsPanel";
import { CarrierMatchesPane } from "@/components/dashboard/CarrierMatchesPane";
import { LaneVolumePane } from "@/components/dashboard/LaneVolumePane";
import { PlaceholderPane } from "@/components/dashboard/PlaceholderPane";
import { MarketIntelligencePane } from "@/components/dashboard/MarketIntelligencePane";
import { PostDuplicatesPane } from "@/components/dashboard/PostDuplicatesPane";
import { type DashboardViewMode, VIEW_MODE_LABELS } from "@/types/dashboardViews";
import { LiveFeedV2 } from "@/components/dashboard/LiveFeedV2";
import { AIRecommendationsPanel } from "@/components/dashboard/AIRecommendationsPanel";
import ManualPendingBanner from "@/components/onboarding/ManualPendingBanner";

import { AtRiskLoadsPanel } from "@/components/dashboard/AtRiskLoadsPanel";
import { RepostPanel } from "@/components/dashboard/RepostPanel";
import { AtlasAssistantPanel } from "@/components/dashboard/AtlasAssistantPanel";
import { GatedSection, UpgradeOverlay } from "@/components/dashboard/UpgradeGate";
import { DocumentUploadGate } from "@/components/dashboard/DocumentUploadGate";
import { DemoHint } from "@/components/demo/DemoHint";

import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useBrokerOffers } from "@/hooks/useBrokerOffers";
import { useLaneData } from "@/hooks/useLaneData";
import { useMarketPressureIndex } from "@/hooks/useMarketPressureIndex";

type Equipment = "Van" | "Reefer" | "Flatbed";
type HaulType = "short" | "medium" | "long" | null;

const EQUIPMENT_DB_MAP: Record<Equipment, string> = {
  Van: "van",
  Reefer: "reefer",
  Flatbed: "flatbed",
};

const HAUL_RANGES: Record<string, { min: number; max: number }> = {
  short: { min: 188, max: 500 },
  medium: { min: 501, max: 1500 },
  long: { min: 1501, max: 4000 },
};

export default function BrokerDashboardV2() {
  usePageMeta({ title: "Broker Dashboard | LaneRunner", description: "LaneRunner broker command center" });
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTop = 0;
  }, []);
  const navigate = useNavigate();
  const { profile } = useAuth();
  const stats = useDashboardStats();
  const { offers, loading: offersLoading } = useBrokerOffers();
  const { loads: myLoads, loading: myLoadsLoading, refetch: myLoadsRefetch } = useMyLoads();

  // Shared filter state for RPM Market Index + Lane Summary Cards
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment>("Van");
  const [selectedHaulType, setSelectedHaulType] = useState<HaulType>(null);

  const haulRange = selectedHaulType ? HAUL_RANGES[selectedHaulType] : null;
  const { lanes, globalAverage, isLoading: lanesLoading } = useLaneData(
    EQUIPMENT_DB_MAP[selectedEquipment],
    haulRange?.min,
    haulRange?.max
  );

  const isLoading = stats.isLoading || offersLoading || lanesLoading;

  useEffect(() => {
    if (!isLoading) {
      const main = document.querySelector("main");
      if (main) main.scrollTop = 0;
    }
  }, [isLoading]);

  const [viewMode, setViewMode] = useState<DashboardViewMode>("my-loads");

  // Pending offers needing response
  const pendingCount = offers.filter(o => o.status === 'pending' || o.status === 'accepted_by_carrier').length;
  const awaitingCount = offers.filter(o => o.status === 'countered').length;

  // Market Pressure Index
  const [matchEquipment, setMatchEquipment] = useState("Van");
  const { score: matchScore, label: matchLabel, labelColor: matchLabelColor } = useMarketPressureIndex(matchEquipment);

  return (
    <>
      <UpgradeOverlay />
      <DocumentUploadGate />
      <div className="flex flex-col gap-2 xl:gap-3 w-full">
        <ManualPendingBanner />



        {/* ── Metrics Row ──────────────────────────────── */}
        <GatedSection>
          <div className="flex flex-nowrap sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 xl:gap-3 overflow-x-auto sm:overflow-visible pb-1 sm:pb-0 snap-x snap-mandatory [&>*]:snap-start [&>*]:min-w-[150px] sm:[&>*]:min-w-0 [&>*]:h-full">
            <MetricCard label="Active Loads" value={stats.loadsPosted} sub={`${stats.loadsExpired} recently expired`} isLoading={stats.isLoading} />
            <MetricCard label="Pending Offers" value={pendingCount} sub={awaitingCount > 0 ? `${awaitingCount} counters awaiting` : "No counters pending"} isLoading={offersLoading} onClick={() => navigate("/my-loads?tab=offers")} />
            <MetricCard label="Platform Loads" value={(stats.activeLoadsOnboard * 3).toLocaleString()} sub="Live marketplace volume" isLoading={stats.isLoading} />
            <MetricCard label="Diesel Fuel Avg" value="$5.58" sub="U.S. Fuel Price/Gallon" badge={<span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-primary">▲ 1.43%</span>} />
            <MetricCard label="Market Pressure Index" value={`${matchScore}%`} sub="True supply vs demand with weighting" badge={<span className={`text-[10px] font-semibold ${matchLabelColor}`}>{matchLabel}</span>} />
          </div>
        </GatedSection>

        {/* ── Main 3-Column Grid ────────────────────────── */}
        <GatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_240px] xl:grid-cols-[260px_1fr_300px] gap-3 xl:gap-4 items-stretch [&>*:nth-child(1)]:order-1 [&>*:nth-child(2)]:order-3 [&>*:nth-child(3)]:order-2 lg:[&>*:nth-child(2)]:order-2 lg:[&>*:nth-child(3)]:order-3">
            {/* LEFT — Heat Map + Lane Summary */}
            <div className="flex flex-col gap-0 rounded-lg border bg-card overflow-hidden">
              <div className="flex items-center gap-1.5 border-b px-3 py-2.5 bg-muted/20">
                <span className="text-[10px] text-accent">⊕</span>
                <DemoHint hintKey="section.rpmMarketIndex" side="right" showIndicator>
                <h4 className="text-caption font-bold text-foreground">RPM Market Index</h4>
                </DemoHint>
              </div>
              <div className="px-3 pt-2 pb-0">
                <HeatMapModule
                  equipment={selectedEquipment}
                  onEquipmentChange={setSelectedEquipment}
                  haulType={selectedHaulType}
                  onHaulTypeChange={setSelectedHaulType}
                  lanes={lanes}
                  globalAverage={globalAverage}
                  isLoading={lanesLoading}
                />
              </div>
              {lanes.slice(0, 2).map((lane, i) => (
                <div key={i} className="border-t mx-0 p-0">
                  <LaneSummaryCard origin={lane.shortName.split(' → ')[0] || '—'} destination={lane.shortName.split(' → ')[1] || '—'} avgRpm={lane.rate} rpmChange={0} postedLoads={lane.volume} equipment={EQUIPMENT_DB_MAP[selectedEquipment]} />
                </div>
              ))}
              {lanes.length === 0 && !lanesLoading && (
                <div className="border-t p-3 text-center text-[11px] text-muted-foreground">No lane data available</div>
              )}
            </div>

            {/* CENTER — Results Pane */}
            <div className="flex flex-col gap-0 h-full">
              {(() => {
                switch (viewMode) {
                  case "my-loads":
                    return <LoadsTableV2 loads={myLoads} loading={myLoadsLoading} refetch={myLoadsRefetch} maxRows={21} />;
                  case "carrier-matches":
                    return <CarrierMatchesPane onBack={() => setViewMode("my-loads")} />;
                  case "lane-volume":
                    return <LaneVolumePane onBack={() => setViewMode("my-loads")} />;
                  case "post-duplicates":
                    return <PostDuplicatesPane onBack={() => setViewMode("my-loads")} />;
                  case "market-intelligence":
                    return <MarketIntelligencePane onBack={() => setViewMode("my-loads")} />;
                  default:
                    return <PlaceholderPane label={VIEW_MODE_LABELS[viewMode]} onBack={() => setViewMode("my-loads")} />;
                }
              })()}
            </div>

            {/* RIGHT — AI Quick Actions */}
            <div className="flex flex-col h-full min-w-0 rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
              <div className="flex items-center justify-between border-b px-3 py-2.5 bg-muted/20">
                <DemoHint hintKey="section.aiQuickActions" side="left" showIndicator>
                <h4 className="text-caption font-bold text-foreground">AI Quick Actions</h4>
                </DemoHint>
              </div>
              <div className="flex-1 p-2.5">
                <AIQuickActionsPanel onViewChange={setViewMode} />
              </div>
            </div>
          </div>
        </GatedSection>

        {/* ── Live Feed ─────────────────────────────────── */}
        <GatedSection>
          <LiveFeedV2 />
        </GatedSection>

        {/* ── Lower Row: 4-column command center ── */}
        <GatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-4 items-stretch">
            <AIRecommendationsPanel equipment={EQUIPMENT_DB_MAP[selectedEquipment]} minMiles={haulRange?.min} maxMiles={haulRange?.max} lanes={lanes} isLoading={lanesLoading} />
            <RepostPanel />
            <AtRiskLoadsPanel />
            <AtlasAssistantPanel hasLoads={myLoads.length > 0} />
          </div>
        </GatedSection>

      </div>
    </>
  );
}
